/**
 * Wire protocol between clients and the ring runtime.
 *
 * Validated with Zod at the boundary in both directions. The engine treats
 * every inbound frame as hostile until it parses: a client-asserted role is a
 * hint, never an authority (spec section 6).
 *
 * Deliberately small. Every message either asks the engine to do something or
 * reports what it did - there is no third category, and nothing here carries
 * auction truth that the engine did not commit.
 *
 * Versioning (ADR-60 cut 1). The server accepts every version in
 * SUPPORTED_PROTOCOL_VERSIONS and records which one a socket speaks. Version 2
 * adds only optional fields and new frame types, so a v1 client keeps working
 * against a v2 ring and nobody is hard-refreshed mid-sale for a field they do
 * not read. A frame type a client does not know is ignored by construction
 * (the client switches on `type`).
 */

import { z } from 'zod';

export const PROTOCOL_VERSION = 2;
/** Every version the ring will answer. Oldest first. */
export const SUPPORTED_PROTOCOL_VERSIONS: readonly number[] = [1, 2];

// ---------------------------------------------------------------------------
// Client -> Server
// ---------------------------------------------------------------------------

/** First frame on every connection. The server answers with SNAPSHOT. */
export const Hello = z.object({
  type: z.literal('HELLO'),
  protocolVersion: z.number().int(),
  actorId: z.string().min(1),
  role: z.enum(['clerk', 'spotter', 'bidder', 'observer']),
  /** Who you are. Production: a Cognito access token; the server derives
   *  the actor and the role it will GRANT from it (the requested role is a
   *  request, never trusted). Dev (ring without Cognito): `dev:<id>:<role>`.
   *  Optional only for 'observer' (Auction TV is public). */
  token: z.string().min(1).optional(),
  /** This device's bidding session id (ADR-60 cut 2 makes it the fencing
   *  token; until then it is recorded and echoed). */
  sessionId: z.string().min(1).optional(),
  /** Present on reconnect: the last seq this client applied. The server still
   *  answers with a full snapshot (history is for audit, the snapshot is for
   *  recovery); the value is telemetry - it says this is a reconnect. */
  lastSeq: z.number().int().nonnegative().optional(),
  /** ADR-70 cut 6b (hitlist 8.2): the surface this client is, e.g. WEB_LIVE
   *  for the sale page. A claim: the ring keeps it only for a bidder, only
   *  from the list a bidder page may claim, and records the clerk's console
   *  as RING_CLERK whatever it says. Lenient: a value that does not parse is
   *  dropped, never a reason to refuse the HELLO. */
  origin: z.string().max(40).optional().catch(undefined),
});

/**
 * ADR-70 cut 6b: the bidder's window at the bid, in CSS pixels, as the
 * client reports it. Evidence on the bid attempt only. Lenient like `origin`:
 * a bad value drops the field, never the bid.
 */
export const Viewport = z.object({
  w: z.number().int().min(1).max(20_000),
  h: z.number().int().min(1).max(20_000),
});

/**
 * ADR-60: the client detected a gap in the public event stream (an EVENT whose
 * `prevSeq` is beyond the last seq it applied) and refuses to apply further
 * events until it holds a fresh authoritative snapshot. The server answers
 * with a personal SNAPSHOT. Never buffered, never replayed: the snapshot IS
 * the recovery.
 */
export const Resync = z.object({
  type: z.literal('RESYNC'),
  lastSeq: z.number().int().nonnegative(),
  /** ADR-70: why the client asked, so the ring can count resyncs by cause
   *  (a gap on the wire, a PONG ahead of the client, a person's button, a
   *  support command). Optional: older clients do not send it. */
  reason: z.enum(['GAP', 'PONG', 'MANUAL', 'SUPPORT']).optional(),
});

/** A bid. The ask is echoed back as a guard so the engine never silently
 *  transforms the amount the bidder actually saw. */
export const PlaceBid = z.object({
  type: z.literal('PLACE_BID'),
  lotId: z.string().min(1),
  atAsk: z.number().int().positive(),
  idempotencyKey: z.string().min(1),
  viewport: Viewport.optional().catch(undefined),
});

/** ADR-42: a bidder's PRIVATE maximum on a lot. Executed by the engine as
 *  ordinary bids at the ask, only as far as needed. The amount never reaches any
 *  other client. Idempotent like PLACE_BID (OUTCOME keyed by idempotencyKey). */
export const PlaceMaxBid = z.object({
  type: z.literal('PLACE_MAX_BID'),
  lotId: z.string().min(1),
  maxAmount: z.number().int().positive(),
  idempotencyKey: z.string().min(1),
  viewport: Viewport.optional().catch(undefined),
});

/** ADR-42: withdraw a standing max (bids already placed by it stand). */
export const CancelMaxBid = z.object({
  type: z.literal('CANCEL_MAX_BID'),
  lotId: z.string().min(1),
  idempotencyKey: z.string().min(1),
});

/** ADR-70: the shape of a client action id - URL-safe, long enough to be a
 *  random id, short enough to key a record. The engine applies the same rule. */
export const CLIENT_ACTION_ID_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

/** Anything a clerk or spotter does. The payload is validated against the
 *  engine's command union server-side; this only fixes the envelope. */
export const FloorCommand = z.object({
  type: z.literal('FLOOR_COMMAND'),
  command: z.record(z.unknown()),
  /**
   * ADR-70: the console's id for this one action, minted once and re-sent
   * unchanged on every retry. With it the ring answers with a COMMAND_RESULT
   * and applies the action at most once (a repeat is answered from the
   * engine's record). Without it the old behaviour holds: ERROR on refusal,
   * silence on success.
   */
  clientActionId: z.string().regex(CLIENT_ACTION_ID_PATTERN).optional(),
});

/**
 * ADR-60 cut 2: this device takes the bidder's bidding lease (spec 8.14). The
 * session id is the one this socket gave at HELLO; other devices of the same
 * bidder become view-only and are told by a YOU frame.
 */
export const ClaimBiddingSession = z.object({
  type: z.literal('CLAIM_BIDDING_SESSION'),
});

/** ADR-60 cut 3: the bidder has read the material updates on `lotId` (null =
 *  sale-wide) up to and including `throughSeq`. */
export const AcknowledgeAnnouncements = z.object({
  type: z.literal('ACKNOWLEDGE_ANNOUNCEMENTS'),
  lotId: z.string().min(1).nullable(),
  throughSeq: z.number().int().positive(),
});

/**
 * ADR-60 cut 6: what a viewer's player reports (brief section 38). Anonymous
 * by construction - the ring aggregates counts per window and never stores a
 * report per bidder. Sent every few seconds while a player exists; a client
 * with no media configured sends NO_PROVIDER once and stops.
 */
export const MediaReport = z.object({
  type: z.literal('MEDIA_REPORT'),
  playerSessionStatus: z.enum(['NO_PROVIDER', 'CONNECTING', 'PLAYING', 'STALLED', 'FAILED', 'STOPPED']),
  streamStatus: z.enum(['UNKNOWN', 'LIVE', 'OFFLINE']),
  videoRendering: z.boolean(),
  audioRendering: z.boolean().optional(),
  droppedFrames: z.number().int().nonnegative().optional(),
  packetLossPct: z.number().min(0).max(100).optional(),
  jitterMs: z.number().nonnegative().optional(),
  bitrateKbps: z.number().nonnegative().optional(),
  width: z.number().int().nonnegative().optional(),
  height: z.number().int().nonnegative().optional(),
});

/**
 * ADR-60 cut 7: the allowlist of things support may ask a client to do (brief
 * section 41). There is deliberately no "run this code" entry and never will
 * be; a new capability is a new name here, in the ring, and in the client.
 */
export const SUPPORT_COMMAND_TYPES = [
  'REFRESH_RUNTIME_STATE', 'RECONNECT_REALTIME', 'RESTART_MEDIA_PLAYER', 'TEST_CAMERA', 'TEST_MICROPHONE',
  'RUN_NETWORK_DIAGNOSTIC', 'CAPTURE_CLIENT_DIAGNOSTICS', 'CLEAR_RUNTIME_CACHE', 'RELOAD_APPLICATION', 'REPORT_DEVICE_INFO',
] as const;
export type SupportCommandType = (typeof SUPPORT_COMMAND_TYPES)[number];

/** The client's answer to a SUPPORT frame. `diagnostics` is the sanitized
 *  bundle of brief section 43 - identifiers and technical metadata, never a
 *  token or payment data; the client builds it from an allowlist. */
export const SupportResult = z.object({
  type: z.literal('SUPPORT_RESULT'),
  commandId: z.string().min(1),
  status: z.enum(['ACKNOWLEDGED', 'SUCCEEDED', 'FAILED']),
  summary: z.string().max(2_000).optional(),
  diagnostics: z.record(z.unknown()).optional(),
});

export const Ping = z.object({
  type: z.literal('PING'),
  clientTs: z.number().int(),
  /** ADR-60: the client's last measured round trip, so the ring can keep
   *  latency percentiles per auction without guessing from its own side. */
  rttMs: z.number().int().nonnegative().optional(),
});

/**
 * ADR-70 cut 6f (hitlist 10): what a bidder's page tells the ring it is
 * about to do on the lot in the ring, for the auctioneer's Online interest
 * panel. Ephemeral: the ring holds it in memory for seconds and never writes
 * it to the log. A page sends it only while the snapshot's `intentShown`
 * says the auctioneer sees it, and never an amount. Viewing is the
 * connection itself and a submitted bid is the bid arriving, so the ring
 * derives both; a page cannot claim either.
 */
export const BIDDER_INTENT_SIGNALS = [
  'BID_BUTTON_HOVERED', 'BID_BUTTON_NOT_HOVERED',
  'BID_CONFIRM_OPEN', 'BID_CONFIRM_CLOSED',
  'OUTBID_CONFIRM_OPEN', 'OUTBID_CONFIRM_CLOSED',
] as const;
export type BidderIntentSignal = (typeof BIDDER_INTENT_SIGNALS)[number];
export const BidderIntent = z.object({
  type: z.literal('INTENT'),
  lotId: z.string().min(1).max(200),
  signal: z.enum(BIDDER_INTENT_SIGNALS),
}).strict();

export const ClientMessage = z.discriminatedUnion('type', [Hello, Resync, PlaceBid, PlaceMaxBid, CancelMaxBid, ClaimBiddingSession, AcknowledgeAnnouncements, MediaReport, SupportResult, FloorCommand, Ping, BidderIntent]);
export type ClientMessage = z.infer<typeof ClientMessage>;

// ---------------------------------------------------------------------------
// Server -> Client
// ---------------------------------------------------------------------------

/** Operator (floor-input) presence as the ring sees it (ADR-60). Derived from
 *  the same heartbeat fact that drives the auto-pause rule. */
export interface OperatorPresenceFrame {
  connected: boolean;
  lastSeenAt: number | null;
}

/** The ring's structured answer to "may I bid, and if not why" (ADR-60). */
export interface EligibilityFrame {
  allowed: boolean;
  reasons: string[];
}

/**
 * The connected bidder's OWN standing (ADR-60). Personal: carried on the
 * snapshot that answers a HELLO or RESYNC and on YOU frames, never on the
 * broadcast snapshot (which is serialised once for the whole crowd).
 */
export interface YouFrame {
  type: 'YOU';
  actorId: string;
  role: 'clerk' | 'spotter' | 'bidder' | 'observer';
  /** ADR-70: the ring seq this standing was computed at, so a client can
   *  order it against EVENT frames instead of trusting arrival order. */
  seq?: number;
  /** True when this bidder holds the current high bid. */
  isHighBidder: boolean;
  /** Seq of the BID_ACCEPTED that set the board, when isHighBidder. */
  highBidSeq: number | null;
  eligibility: EligibilityFrame;
  /** ADR-60 cut 2: whether THIS device holds the bidder's bidding lease. */
  biddingSession?: { active: boolean; sessionId: string; policy: string };
  /** ADR-60 cut 2: this bidder's own pending (confirm-mode) bid, if any. */
  pending?: { pendingBidId: string; amount: number; expiresAt: number | null } | null;
  /** ADR-60 cut 3: material updates on the lot in the ring (or sale-wide)
   *  this bidder must read before bidding, oldest first. */
  requiredAcknowledgements?: { seq: number; lotId: string | null; text: string; category: string; severity: string }[];
  /**
   * HN-030: this bidder's own standing lot by lot, for a page that lists many
   * lots at once. `isHighBidder` above speaks only for the lot in the ring;
   * a TIMED sale has many lots open and the public wire names nobody, so
   * without this a bidder cannot tell which lots they hold. One entry per lot
   * where the bidder holds the high bid (a timed lot, or the lot in the ring)
   * or has an active maximum. `max` is the bidder's OWN maximum, never
   * anyone else's. Absent on a ring that predates it; empty when none.
   */
  lots?: { lotId: string; isHighBidder: boolean; max: number | null }[];
}

/**
 * ADR-60 cut 2: how a HELD bid ended (private, to the bidder who placed it).
 * A PENDING outcome is immutable; this is the later fact. `acceptedSeq` is the
 * BID_ACCEPTED it became when the operator took it.
 */
export interface BidResolutionFrame {
  type: 'BID_RESOLUTION';
  pendingBidId: string;
  idempotencyKey?: string;
  result: 'ACCEPTED' | 'REJECTED' | 'SUPERSEDED_BY_FLOOR' | 'ASK_CHANGED' | 'LOT_CLOSED' | 'LOT_CHANGED' | 'EXPIRED';
  seq: number;
  acceptedSeq?: number;
}

/** Everything a client needs to render the ring from cold. Sent on connect,
 *  on reconnect when the gap is too large to replay, and whenever a client
 *  falls too far behind. */
export interface Snapshot {
  type: 'SNAPSHOT';
  seq: number;
  // ---- ADR-70 ----
  /** The ring and sale this stream belongs to. A client pins them on its first
   *  snapshot and drops any frame that names another ring. */
  ringId?: string;
  saleId?: string;
  /** The seq of the standing high bid (null: no bid). With lotId, the view a
   *  console sends back as `expected` on HAMMER, SOLD, PASS and the rest. */
  highBidSeq?: number | null;
  /** The ring answers keyed FLOOR_COMMANDs with COMMAND_RESULT. A console
   *  waits for answers only when this is true (an older ring never sends one). */
  commandResults?: boolean;
  lotId: string | null;
  lotState: string;
  currentHighBid: number | null;
  currentAsk: number | null;
  highBidderRef: string | null;
  onlineStatus: 'OPEN' | 'PAUSED';
  pauseReason: string | null;
  /** Non-null once the ring is sealed (FINALIZE_SALE). */
  finalizedSeq?: number | null;
  // ---- ADR-42 ----
  /** LIVE | TIMED | LIVE_WITH_PREBIDDING - how lots progress and close. */
  mode?: 'LIVE' | 'TIMED' | 'LIVE_WITH_PREBIDDING';
  /** The provisioning generation this engine was created for (ADR-48). Lets an
   *  observer confirm the running engine is the CURRENT generation, not a stale
   *  one that happens to share the mode. null on pre-ADR-48 rings. */
  provisioningId?: string | null;
  /** Engine time when this snapshot was built. Clients render countdowns as
   *  `scheduledCloseAt - (serverNow + elapsedSinceReceipt)`: a DISPLAY aid only -
   *  the authoritative close is decided by engine time on commit. */
  serverNow?: number;
  /** TIMED: every lot's public position and schedule. No identity, no reserve. */
  timedLots?: TimedLotSnapshot[];
  // ---- ADR-60 ----
  /** The protocol version the ring speaks. */
  protocolVersion?: number;
  /** The lease epoch of the engine that built this snapshot. A client that
   *  sees it change has been failed over to a replacement engine. */
  engineEpoch?: number;
  operator?: OperatorPresenceFrame;
  /** Present only on a personal snapshot (HELLO / RESYNC). */
  you?: Omit<YouFrame, 'type'>;
  // ---- ADR-60 cut 2 ----
  /** The ring accepts internet bids only when the operator takes them. */
  confirmMode?: boolean;
  /** The ring's one pending internet bid, as the crowd may see it. */
  pendingBid?: { pendingBidId: string; amount: number; bidderRef: string; expiresAt: number | null } | null;
  /** ADR-60 cut 5: the lots selling on the one bid (null = a single lot). */
  sellingGroup?: { lotIds: string[]; mode: 'ALL_TO_GO' | 'X_MONEY' | 'CHOICE'; multiplier: number } | null;
  /**
   * ADR-64: the ring's most recent disposition, so a client joining between
   * lots can show the last result before the next lot opens. Public by
   * construction: the same facts travel on the disposition event itself.
   * null until the ring has disposed a lot.
   */
  previousLot?: { lotId: string; outcome: 'SOLD' | 'NO_SALE' | 'PASSED' | 'VOIDED'; price: number | null; seq: number; at: number } | null;
  /**
   * ADR-60 cut 6: the viewer-safe media configuration (brief section 36).
   * Public by construction - it goes to every socket - so it never carries a
   * publisher credential. `provider` NONE means no player is offered. A media
   * failure never touches anything else on this snapshot.
   */
  media?: {
    provider: string;
    audioEnabled: boolean;
    videoEnabled: boolean;
    streamId: string | null;
    viewerConfig: Record<string, unknown> | null;
    redundancyMode: 'NONE' | 'DUAL_TRACK' | 'DUAL_PROVIDER';
    forceLiveVideo: boolean;
  };
  /**
   * ADR-70 cut 6f (hitlist 10, Q12): what of a bidder's activity on this page
   * the auctioneer is shown, so the page can say so before it sends anything.
   * Public on purpose: it is the notice. null (or absent, on an older ring)
   * while the tenant's switch is off: the page sends no intent at all.
   */
  intentShown?: IntentShown | null;
}

/** ADR-70 cut 6f: the parts of a bidder's activity the auctioneer sees. */
export interface IntentShown {
  /** That the bidder has the sale open. */
  presence: boolean;
  /** Their pointer resting on Bid for the lot in the ring. */
  hover: boolean;
  /** Their bid confirmation (or outbid prompt) open. */
  confirm: boolean;
  /** That they just pressed Bid. */
  submitted: boolean;
  /** By name or paddle (otherwise "Online bidder 3"). */
  identity: boolean;
}

export interface TimedLotSnapshot {
  lotId: string;
  phase: 'OPEN' | 'EXTENDED' | 'CLOSED_PENDING' | 'SOLD' | 'NO_SALE';
  scheduledCloseAt: number;
  extensions: number;
  currentHighBid: number | null;
  currentAsk: number | null;
  highBidderRef: string | null;
  closeGroupId: string | null;
}

/**
 * One committed, public, redacted event. Clients render strictly in seq order
 * and discard duplicates.
 *
 * `prevSeq` (ADR-60) is the seq of the previous event THIS WIRE carried. The
 * ring seq is gapless in the log, but a wire is a projection of it: private
 * events (a max placed, an approval change) consume seqs the public wire never
 * shows. So "did I miss something" cannot be `seq == lastSeq + 1`; it is
 * `prevSeq <= lastSeq`. A client whose lastSeq is below prevSeq has a gap and
 * must RESYNC rather than apply.
 */
export interface EventFrame {
  type: 'EVENT';
  seq: number;
  prevSeq?: number;
  /** ADR-70: the envelope's identity. The aggregate is the ring (`ringId`),
   *  its version is `seq`, and `stream` names the projection this wire
   *  carries - the public crowd's, or the floor's. The sale rides on the
   *  SNAPSHOT only (one socket is one ring of one sale), which keeps every
   *  event frame small at crowd size. */
  ringId?: string;
  stream?: 'public' | 'floor';
  event: Record<string, unknown>;
}

/**
 * ADR-70: the private answer to one keyed FLOOR_COMMAND - accepted, refused,
 * or a repeat answered from the engine's record. Sent to the socket that sent
 * the command, for every keyed command, including refusals before the engine
 * and an engine failure (INTERNAL_RETRY: the same id may be sent again).
 *
 * STALE_AUCTION_STATE means the console decided on a screen the ring has moved
 * past; a personal SNAPSHOT follows this frame, and `authoritativeSeq` is the
 * ring version the refusal was judged at.
 */
export interface CommandResultFrame {
  type: 'COMMAND_RESULT';
  clientActionId: string;
  commandType: string;
  accepted: boolean;
  reason?: string;
  /** The ring's last seq after the action (accepted), when it moved. */
  seq?: number;
  /** True when this id had already been answered and nothing was re-applied. */
  duplicate?: boolean;
  authoritativeSeq: number;
}

/**
 * ADR-70 cut 6e (hitlist 9.2): what the clerk's console was shown of the
 * maxes on a lot, sent to the one socket that asked (VIEW_MAX_BIDS) right
 * after its COMMAND_RESULT. Never broadcast: every other floor screen sees
 * only that a view happened (MAX_BID_VIEWED without `viewed`). The fields on
 * each entry are the ones the viewer's role may see: a clerk gets the
 * bidder, an admin the amount and what is left above the high bid too
 * (`remaining`, null with no high bid to measure from). `seq` is the
 * MAX_BID_VIEWED that recorded it; `asOf` the ring time of the view.
 */
export interface MaxBidsFrame {
  type: 'MAX_BIDS';
  lotId: string;
  seq: number;
  asOf: number;
  viewerRole: 'admin' | 'clerk';
  scopes: string[];
  activeCount: number;
  viewed: { placedSeq: number; bidderId?: string; maxAmount?: number; remaining?: number | null }[];
}

/** ADR-70 cut 6f: what the Online interest panel shows a bidder doing, the
 *  strongest first. BID_SUBMITTED is the ring's own (the bid arrived). */
export type IntentKind = 'BID_SUBMITTED' | 'BID_CONFIRM_OPEN' | 'OUTBID_CONFIRM_OPEN' | 'BID_BUTTON_HOVERED';

/** ADR-70 cut 6f: one bidder as the panel names them. `handle` is a number
 *  the ring gives each bidder for as long as it runs ("Online bidder 3");
 *  `bidderId` is there only when the tenant shows bidder identity, for the
 *  console to turn into a paddle from the registrations. `device` is the
 *  class the ring read from the connection (cut 6b). */
export interface InterestBidder {
  handle: number;
  bidderId?: string;
  device?: 'DESKTOP' | 'TABLET' | 'MOBILE';
}

/**
 * ADR-70 cut 6f (hitlist 10.2 and 11): the auctioneer's Online interest
 * panel, sent to the clerk's console (never a spotter, never the public)
 * while the tenant's operator intent switch is on, at most twice a second
 * and only when something changed. Built from memory, never from the log:
 * nothing here is recorded. A part the tenant's controls do not show is null
 * (counts, presence) or simply absent (signals of that kind).
 */
export interface InterestFrame {
  type: 'INTEREST';
  /** The lot in the ring the signals are about; null between lots. */
  lotId: string | null;
  asOf: number;
  /** Everyone off the floor connected to this ring, signed in or not. */
  watching: number | null;
  /** Approved bidders with the sale open. */
  biddersPresent: number | null;
  /** Who they are, longest connected first; capped at `presentCap`. */
  present: InterestBidder[] | null;
  presentCap: number;
  /** Bidders doing something on `lotId` now, strongest then newest first. */
  signals: (InterestBidder & { kind: IntentKind; since: number })[];
}

/**
 * The private answer to one bid intent.
 *
 * Separate from the public EVENT stream on purpose: under fanout jitter the
 * bidder still gets an unambiguous personal answer, and it is keyed by
 * idempotency key so a reconnecting client can resolve it (spec 7.3).
 */
export interface OutcomeFrame {
  type: 'OUTCOME';
  idempotencyKey: string;
  accepted: boolean;
  seq?: number;
  reason?: string;
  /** ADR-60 cut 2: the bid was HELD for the operator (accepted is false, reason
   *  PENDING_RING_ACCEPTANCE). A BID_RESOLUTION follows when it ends. */
  pending?: boolean;
  pendingBidId?: string;
}

export interface Pong {
  /** ADR-60 cut 11: the last seq THIS wire carried when the pong was sent.
   *  A client whose applied seq is behind it has missed a frame with nothing
   *  behind it to expose the gap; it resyncs. */
  lastSeq?: number;
  type: 'PONG';
  clientTs: number;
  serverTs: number;
  /** ADR-60: when the ring took the PING off the wire and when it answered,
   *  so a client can separate transport time from ring processing time. */
  serverReceivedAt?: number;
  serverSentAt?: number;
}

export interface ErrorFrame {
  type: 'ERROR';
  message: string;
  /** ADR-70 cut 5f: a machine-readable reason, when the page can act on it.
   *  RING_NOT_ASSIGNED: the office assigned this staffer to other rings of
   *  the sale (`rings`); the socket is closed 4401 'ring not assigned'. */
  code?: 'RING_NOT_ASSIGNED';
  rings?: readonly string[];
}

/** ADR-70 cut 5f: the close reason (code 4401) when the ring staff roster
 *  binds this staffer to other rings. A reconnect is refused the same way,
 *  so a client stops at once instead of retrying a sign-in. */
export const RING_NOT_ASSIGNED_CLOSE_REASON = 'ring not assigned';

/** ADR-60 cut 7: a support command for THIS client (server to client). */
export interface SupportFrame {
  type: 'SUPPORT';
  commandId: string;
  command: SupportCommandType;
  /** Shown to the person, verbatim: why support is doing this. */
  reason: string;
  incidentNumber?: string;
}

export type ServerMessage = Snapshot | EventFrame | OutcomeFrame | Pong | ErrorFrame | YouFrame | BidResolutionFrame | SupportFrame | CommandResultFrame | MaxBidsFrame | InterestFrame;

/** Parse an inbound client frame. Returns null rather than throwing, because a
 *  malformed frame is a connection to close, not an exception to propagate. */
export function parseClientMessage(raw: string): ClientMessage | null {
  try {
    const result = ClientMessage.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}
